import { useTranslation } from 'react-i18next';

import GlyphChevronDown from '@/assets/icons/glyph_chevron_down.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { Switch } from '@/components/atoms/Switch';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '@/components/molecules/Menu';
import { type AuthUser, usePermissions } from '@/contexts/auth';
import { DEMO_MODE_KEY, useDemoMode } from '@/contexts/demo/DemoContext';

import styles from './ClientNav.module.scss';

export type ClientNavVariant = 'header' | 'mobile';

export interface ClientNavProps {
    user: AuthUser;
    variant: ClientNavVariant;
    onLogout: () => Promise<unknown>;
}

function getUserInitial(user: AuthUser): string {
    const first = user.firstName?.trim().charAt(0);
    const last = user.lastName?.trim().charAt(0);
    const email = user.email?.trim().charAt(0);
    if (first) return first.toUpperCase();
    if (last) return last.toUpperCase();
    if (email) return email.toUpperCase();
    return '?';
}

function getDisplayName(user: AuthUser, fallback: string): string {
    if (user.firstName || user.lastName) {
        return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    }
    return fallback;
}

export default function ClientNav(props: ClientNavProps) {
    const { user, variant, onLogout } = props;
    const { t } = useTranslation();
    const { isOptoScaleAdmin } = usePermissions();
    const { isDemoMode, toggleDemoMode } = useDemoMode();
    const direction = variant === 'mobile' ? 'up' : 'down';
    const initial = getUserInitial(user);
    const displayName = getDisplayName(user, t('molecules.clientNav.profile'));

    const handleDemoModeToggle = (checked: boolean) => {
        // Update localStorage directly to ensure it's saved
        if (typeof window !== 'undefined') {
            try {
                localStorage.setItem(DEMO_MODE_KEY, String(checked));
            } catch {
                // Ignore storage errors
            }
        }
        toggleDemoMode();
        // Only reload when disabling demo mode (checked === false)
        if (!checked) {
            window.location.reload();
        }
    };

    const handleLogoutClick = async (): Promise<void> => {
        try {
            await onLogout();
        } catch {
            // The reload below shows whether the session ended; there is nothing else to tell.
        } finally {
            window.location.reload();
        }
    };

    const avatarCircle = (
        <span className={styles.avatarCircle} aria-hidden>
            {initial}
        </span>
    );

    return (
        <div className={styles.root} data-variant={variant}>
            <Menu direction={direction}>
                <MenuTrigger
                    variant={variant === 'header' ? 'unstyled' : 'sidebar'}
                    aria-label={variant === 'header' ? displayName : undefined}
                    className={variant === 'header' ? styles.toggleHeader : undefined}>
                    {variant === 'header' ? (
                        avatarCircle
                    ) : (
                        <>
                            {avatarCircle}
                            <span className={styles.mobileTextBlock}>
                                <span className={styles.userName}>{displayName}</span>
                                <span className={styles.userEmail}>{user.email}</span>
                            </span>
                            <span className={styles.chevronUp} aria-hidden>
                                <SvgIcon icon={GlyphChevronDown} size="small" />
                            </span>
                        </>
                    )}
                </MenuTrigger>
                <MenuContent
                    align="end"
                    appearance={variant === 'mobile' ? 'sidebar' : 'default'}
                    width="wide">
                    {variant === 'header' && (
                        <div className={styles.menuUserInfo}>
                            <span className={styles.menuUserName}>{displayName}</span>
                            <span className={styles.menuUserEmail}>{user.email}</span>
                        </div>
                    )}
                    {isOptoScaleAdmin && (
                        <MenuItem as="div" keepOpen>
                            <div className={styles.menuItemContent}>
                                <span>{t('molecules.clientNav.demoMode')}</span>
                                <Switch
                                    checked={isDemoMode}
                                    onCheckedChange={handleDemoModeToggle}
                                    aria-label={t('molecules.clientNav.toggleDemoMode')}
                                />
                            </div>
                        </MenuItem>
                    )}
                    <MenuItem onClick={handleLogoutClick}>
                        {t('molecules.clientNav.logout')}
                    </MenuItem>
                </MenuContent>
            </Menu>
        </div>
    );
}
